import { useEffect, useState } from 'react'
import { resolveStoredProjectThumbnailSrc } from '@/features/project/lib/project-thumbnail-source'
import {
  getProjectThumbnailObjectUrl,
  subscribeProjectThumbnails,
} from '@/features/project/lib/project-thumbnail-store'

/**
 * The image a project card is allowed to show, in priority order:
 *
 *   1. the stored server thumbnail, routed through the studio-origin proxy
 *      when it lives on another origin (never handed cross-origin to `<img>`),
 *   2. the thumbnail the studio captured itself from its own render of the
 *      project, delivered as a same-origin `blob:` URL,
 *   3. `null` — the card shows its honest "no preview yet" placeholder.
 *
 * Passing no `projectId` collapses this to rule 1's same-origin subset, which
 * is the behaviour every non-project caller (templates, library) already had.
 */
export function useProjectThumbnailSrc(
  imageSrc: string | null | undefined,
  projectId?: string | null,
): string | null {
  const stored = resolveStoredProjectThumbnailSrc(imageSrc, projectId)
  const [captured, setCaptured] = useState<string | null>(null)

  useEffect(() => {
    // A stored thumbnail wins; do not pay for an IndexedDB read.
    if (stored || !projectId) {
      setCaptured(null)
      return
    }
    let cancelled = false
    const load = () => {
      void getProjectThumbnailObjectUrl(projectId).then((url) => {
        if (!cancelled) setCaptured(url)
      })
    }
    load()
    const unsubscribe = subscribeProjectThumbnails((changedId) => {
      if (changedId === projectId) load()
    })
    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [projectId, stored])

  return stored ?? captured
}
